import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface JsonFileUploadProps {
  onDataLoaded: (data: any) => void;
}

export const JsonFileUpload = ({ onDataLoaded }: JsonFileUploadProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a JSON file",
        variant: "destructive"
      });
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      onDataLoaded(data);
      
      toast({
        title: "Data loaded successfully",
        description: "Form has been pre-filled with contract data"
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error parsing JSON:', error);
      toast({
        title: "Error reading file",
        description: "Invalid JSON format",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Fill from JSON</CardTitle>
        <CardDescription>
          Upload a JSON file extracted from your contract PDF using Gemini or Copilot
        </CardDescription>
      </CardHeader>
      <CardContent>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileUpload}
          className="hidden"
          id="json-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload JSON File
        </Button>
      </CardContent>
    </Card>
  );
};
